import React, { useState } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    Alert,
    Button,
    Stack,
    TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import QRCodeScanner from "../components/WorkOrders/QRCode/QRCodeScanner";
import DoorWorkOrderDialog from "../components/WorkOrders/Doors/DoorWorkOrderDialog";
import PanelOrderDialog from "../components/WorkOrders/Panels/PanelOrderDialog";
import WindowWorkOrderDialog from "../components/WorkOrders/RFWindows/WindowWorkOrderDialog";
import WaveguideWorkOrderDialog from "../components/WorkOrders/Waveguides/WaveguideWorkOrderDialog";

// Import data services
import DoorDataService from "../services/Door.services";
import PanelDataService from "../services/Panel.services";
import RFWindowDataService from "../services/RFWindow.services";
import WaveguideDataService from "../services/Waveguide.services";

function QRCodeWorkOrderScan(props) {
    const { user, token, handleOpenSnackbar, darkState } = props;

    const [scanError, setScanError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [orderType, setOrderType] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [testQRData, setTestQRData] = useState("");
    const [isScanning, setIsScanning] = useState(false);

    // Parse QR code data and fetch the work order
    const handleQRCodeScanned = async (qrData) => {
        setScanError(null);
        setIsLoading(true);
        setIsScanning(false); // Stop camera immediately when QR is detected

        try {
            // Parse QR code data - format: "projectNumber-TYPE-WorkOrderCount-hexId"
            // Example: "24425-RFW-1-00000005"
            const parts = qrData.trim().split("-");
            if (parts.length !== 4) {
                setScanError(
                    "Invalid QR code format. Expected format: projectNumber-TYPE-WorkOrderCount-hexId (e.g., 24425-RFW-1-00000005)"
                );
                setIsLoading(false);
                return;
            }

            const projectNumber = parts[0].trim();
            const typeCode = parts[1].trim().toUpperCase();
            const workOrderCount = parts[2].trim();
            const hexId = parts[3].trim();

            // Convert hex ID to decimal
            let decimalId;
            try {
                decimalId = parseInt(hexId, 16);
            } catch (e) {
                setScanError(
                    `Invalid hex ID format: ${hexId}. Must be valid hexadecimal.`
                );
                setIsLoading(false);
                return;
            }

            const workOrderNumber = qrData.trim();
            const workOrderId = decimalId.toString();

            // Determine the work order type based on the type code
            const type = determineWorkOrderType(typeCode);

            if (!type) {
                setScanError(
                    `Unable to determine work order type for: ${workOrderNumber}`
                );
                setIsLoading(false);
                return;
            }

            // Fetch the work order based on type
            let order = null;
            switch (type) {
                case "door":
                    order = await DoorDataService.getDoor(workOrderId);
                    break;
                case "panel":
                    order = await PanelDataService.getPanel(workOrderId, token);
                    break;
                case "window":
                    order = await RFWindowDataService.getRFWindow(
                        workOrderId,
                        token
                    );
                    break;
                case "waveguide":
                    order = await WaveguideDataService.getWaveguide(
                        workOrderId,
                        token
                    );
                    break;
                default:
                    setScanError("Unknown work order type");
                    setIsLoading(false);
                    return;
            }

            if (order.data) {
                let orderData = order.data;
                // Transform waveguide data to match dialog field names
                if (type === "waveguide") {
                    orderData = transformWaveguideData(orderData);
                }
                setSelectedOrder(orderData);
                setOrderType(type);
                setDialogOpen(true);
            } else {
                setScanError(
                    `Work order not found: ${workOrderNumber} (ID: ${workOrderId})`
                );
                setIsScanning(true); // Resume if work order not found
            }
        } catch (error) {
            console.error("Error processing QR code:", error);
            setScanError(
                `Error loading work order: ${error.message || "Unknown error"}`
            );
            setIsScanning(true); // Resume if there's an error
        } finally {
            setIsLoading(false);
        }
    };

    // Determine work order type from the type code
    // Type codes: RFW (RF Window), DOOR, PNL (Panel), WGD (Waveguide)
    const determineWorkOrderType = (typeCode) => {
        const code = typeCode.toUpperCase();

        if (code === "RFW") return "window";
        if (code === "DOR") return "door";
        if (code === "PNL") return "panel";
        if (code === "WGD") return "waveguide";

        // Unknown type code
        return null;
    };

    // Close dialog and reset
    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedOrder(null);
        setOrderType(null);
        setScanError(null);
    };

    // Special handler for DoorWorkOrderDialog which uses old prop names
    const handleDoorDialogClose = () => {
        handleDialogClose();
    };

    // Dummy setter for door work order state (required by DoorWorkOrderDialog)
    const setDoorWorkOrder = () => {};

    // Transform waveguide data to match dialog expectations
    const transformWaveguideData = (waveguideData) => {
        if (!waveguideData) return null;
        return {
            ...waveguideData,
            due_date: waveguideData.due, // API returns 'due', dialog expects 'due_date'
            honeycomb_type: waveguideData.honeycomb, // API returns 'honeycomb', dialog expects 'honeycomb_type'
            frame_type:
                waveguideData.frame && waveguideData.frame.description
                    ? waveguideData.frame.description
                    : "—", // API returns 'frame' object, dialog expects 'frame_type' string
            list: waveguideData.items
                ? waveguideData.items.map((item) => ({
                      // API returns 'items', dialog expects 'list'
                      id: item.id,
                      qty: item.quantity,
                      size: {
                          width: item.width,
                          height: item.height,
                          diameter: item.diameter,
                      },
                  }))
                : [], // API returns 'items', dialog expects 'list'
        };
    };

    // Handle work order actions (marked checked/unchecked)
    const handleMarkChecked = async (orderId, itemId) => {
        try {
            switch (orderType) {
                case "door":
                    await DoorDataService.markItemChecked(
                        orderId,
                        itemId,
                        token
                    );
                    break;
                case "panel":
                    await PanelDataService.markPanelItemChecked(
                        orderId,
                        itemId,
                        token
                    );
                    break;
                case "window":
                    await RFWindowDataService.markWindowItemChecked(
                        orderId,
                        itemId,
                        token
                    );
                    break;
                case "waveguide":
                    await WaveguideDataService.markWaveguideItemChecked(
                        orderId,
                        itemId,
                        token
                    );
                    break;
                default:
                    break;
            }
            handleOpenSnackbar("success", "Item marked as checked");
        } catch (error) {
            console.error("Error marking item checked:", error);
            handleOpenSnackbar("error", "Failed to mark item as checked");
        }
    };

    const handleMarkUnchecked = async (orderId, itemId) => {
        try {
            switch (orderType) {
                case "door":
                    await DoorDataService.markItemUnchecked(
                        orderId,
                        itemId,
                        token
                    );
                    break;
                case "panel":
                    await PanelDataService.markPanelItemUnchecked(
                        orderId,
                        itemId,
                        token
                    );
                    break;
                case "window":
                    await RFWindowDataService.markWindowItemUnchecked(
                        orderId,
                        itemId,
                        token
                    );
                    break;
                case "waveguide":
                    await WaveguideDataService.markWaveguideItemUnchecked(
                        orderId,
                        itemId,
                        token
                    );
                    break;
                default:
                    break;
            }
            handleOpenSnackbar("success", "Item marked as unchecked");
        } catch (error) {
            console.error("Error marking item unchecked:", error);
            handleOpenSnackbar("error", "Failed to mark item as unchecked");
        }
    };

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Stack spacing={3}>
                {/* Header */}
                <Typography
                    variant="h3"
                    color="text.primary"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: "2rem", sm: "2.5rem" }, // Larger font size
                        textAlign: "center",
                    }}
                >
                    SCAN QR CODE
                </Typography>

                {/* Main scanning area */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        border: "2px solid",
                        borderColor: "primary.main",
                    }}
                >
                    <QRCodeScanner
                        onQRCodeScanned={handleQRCodeScanned}
                        disabled={isLoading}
                        isScanning={isScanning}
                        setIsScanning={setIsScanning}
                    />

                    {scanError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {scanError}
                            <Box sx={{ mt: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setScanError(null)}
                                >
                                    Dismiss
                                </Button>
                            </Box>
                        </Alert>
                    )}
                </Paper>

                {/* Info section */}
                <Paper elevation={0} sx={{ p: 2, bgcolor: "action.hover" }}>
                    <Typography variant="subtitle2" gutterBottom>
                        <strong>How to use:</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        • Click "Start Scanning" to activate the camera
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        • Point the camera at a QR code to scan it
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        • The work order will automatically open
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                    >
                        <strong>QR Format:</strong>{" "}
                        projectNumber-TYPE-WorkOrderCount-hexId
                    </Typography>
                </Paper>

                {/* Work Order Lookup section */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        bgcolor: "action.hover",
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Stack spacing={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            <strong>Manually Lookup Work Order:</strong>
                        </Typography>
                        <TextField
                            label="Enter Work Order Number"
                            value={testQRData}
                            onChange={(e) => setTestQRData(e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="e.g., 24425-RFW-1-00000005"
                        />
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleQRCodeScanned(testQRData)}
                            disabled={isLoading}
                        >
                            Lookup Work Order
                        </Button>
                    </Stack>
                </Paper>
            </Stack>

            {/* Work Order Dialogs - Always render but only show when appropriate */}
            {orderType === "door" && (
                <DoorWorkOrderDialog
                    openDoorWorkOrderDialog={dialogOpen && orderType === "door"}
                    setOpenDoorWorkOrderDialog={handleDoorDialogClose}
                    order={selectedOrder}
                    user={user}
                    darkState={darkState}
                    setDoorWorkOrder={setDoorWorkOrder}
                    onMarkChecked={handleMarkChecked}
                    onMarkUnchecked={handleMarkUnchecked}
                />
            )}

            {orderType === "panel" && (
                <PanelOrderDialog
                    open={dialogOpen && orderType === "panel"}
                    onClose={handleDialogClose}
                    order={selectedOrder}
                    user={user}
                    onMarkChecked={handleMarkChecked}
                    onMarkUnchecked={handleMarkUnchecked}
                />
            )}

            {orderType === "window" && (
                <WindowWorkOrderDialog
                    open={dialogOpen && orderType === "window"}
                    onClose={handleDialogClose}
                    order={selectedOrder}
                    user={user}
                    onMarkChecked={handleMarkChecked}
                    onMarkUnchecked={handleMarkUnchecked}
                />
            )}

            {orderType === "waveguide" && (
                <WaveguideWorkOrderDialog
                    open={dialogOpen && orderType === "waveguide"}
                    onClose={handleDialogClose}
                    order={selectedOrder}
                    user={user}
                    onMarkChecked={handleMarkChecked}
                    onMarkUnchecked={handleMarkUnchecked}
                />
            )}
        </Container>
    );
}

export default QRCodeWorkOrderScan;
